import { useState, useEffect, useCallback, useMemo } from 'react';
import { salesTeamConfigApi } from '../services/salesTeamConfigApi';
import type { SalesTeam, SalesManager, TeamAssignment } from '../types/salesTeamConfig';

interface UseSalesTeamConfigReturn {
    teams: SalesTeam[];
    managers: SalesManager[];
    assignments: TeamAssignment[];
    unassignedManagers: SalesManager[];
    assignedByTeam: Map<number, { team: SalesTeam; members: TeamAssignment[] }>;
    companyCodesInUse: string[];
    loading: boolean;
    error: string | null;
    searchTerm: string;
    selectedTeamId: number | null;
    selectedCompanyCode: string | null;
    setSearchTerm: (term: string) => void;
    setSelectedTeamId: (id: number | null) => void;
    setSelectedCompanyCode: (code: string | null) => void;
    assignManager: (salesManagerId: string, salesTeamId: number) => Promise<void>;
    unassignManager: (salesManagerId: string) => Promise<void>;
    createTeam: (name: string, description: string | null) => Promise<void>;
    updateTeam: (teamId: number, name: string, description: string | null, isActive?: boolean) => Promise<void>;
    refresh: () => void;
}

export function useSalesTeamConfig(): UseSalesTeamConfigReturn {
    const [teams, setTeams] = useState<SalesTeam[]>([]);
    const [managers, setManagers] = useState<SalesManager[]>([]);
    const [assignments, setAssignments] = useState<TeamAssignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
    const [selectedCompanyCode, setSelectedCompanyCode] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await salesTeamConfigApi.getData();
            setTeams(data.teams);
            setManagers(data.managers);
            setAssignments(data.assignments);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const assignedManagerIds = useMemo(
        () => new Set(assignments.map(a => a.SalesManagerID)),
        [assignments]
    );

    const unassignedManagers = useMemo(() => {
        let result = managers.filter(m => !assignedManagerIds.has(m.SalesManagerID));
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.SalesManagerName.toLowerCase().includes(lower) ||
                m.SalesManagerCode.toLowerCase().includes(lower) ||
                (m.SalesManagerEmail?.toLowerCase().includes(lower) ?? false)
            );
        }
        if (selectedCompanyCode) {
            result = result.filter(m => m.CompanyCode === selectedCompanyCode);
        }
        return result;
    }, [managers, assignedManagerIds, searchTerm, selectedCompanyCode]);

    const assignedByTeam = useMemo(() => {
        const map = new Map<number, { team: SalesTeam; members: TeamAssignment[] }>();

        for (const team of teams) {
            map.set(team.SalesTeamID, { team, members: [] });
        }

        for (const assignment of assignments) {
            const entry = map.get(assignment.SalesTeamID);
            if (entry) {
                entry.members.push(assignment);
            }
        }

        // Apply filters
        if (searchTerm || selectedCompanyCode || selectedTeamId) {
            const lower = searchTerm.toLowerCase();
            const filtered = new Map<number, { team: SalesTeam; members: TeamAssignment[] }>();

            for (const [teamId, entry] of map) {
                if (selectedTeamId && teamId !== selectedTeamId) continue;

                let members = entry.members;
                if (searchTerm) {
                    members = members.filter(m =>
                        m.SalesManagerName.toLowerCase().includes(lower) ||
                        m.SalesManagerCode.toLowerCase().includes(lower) ||
                        (m.SalesManagerEmail?.toLowerCase().includes(lower) ?? false)
                    );
                }
                if (selectedCompanyCode) {
                    members = members.filter(m => m.CompanyCode === selectedCompanyCode);
                }

                if (members.length > 0 || (!searchTerm && !selectedCompanyCode)) {
                    filtered.set(teamId, { team: entry.team, members });
                }
            }
            return filtered;
        }

        return map;
    }, [teams, assignments, searchTerm, selectedTeamId, selectedCompanyCode]);

    const companyCodesInUse = useMemo(() => {
        const codes = new Set(managers.map(m => m.CompanyCode));
        return Array.from(codes).sort();
    }, [managers]);

    const assignManager = useCallback(async (salesManagerId: string, salesTeamId: number) => {
        await salesTeamConfigApi.assignManager(salesManagerId, salesTeamId);
        await fetchData();
    }, [fetchData]);

    const unassignManager = useCallback(async (salesManagerId: string) => {
        await salesTeamConfigApi.unassignManager(salesManagerId);
        await fetchData();
    }, [fetchData]);

    const createTeam = useCallback(async (name: string, description: string | null) => {
        await salesTeamConfigApi.createTeam(name, description);
        await fetchData();
    }, [fetchData]);

    const updateTeam = useCallback(async (teamId: number, name: string, description: string | null, isActive?: boolean) => {
        await salesTeamConfigApi.updateTeam(teamId, name, description, isActive);
        await fetchData();
    }, [fetchData]);

    return {
        teams,
        managers,
        assignments,
        unassignedManagers,
        assignedByTeam,
        companyCodesInUse,
        loading,
        error,
        searchTerm,
        selectedTeamId,
        selectedCompanyCode,
        setSearchTerm,
        setSelectedTeamId,
        setSelectedCompanyCode,
        assignManager,
        unassignManager,
        createTeam,
        updateTeam,
        refresh: fetchData,
    };
}
